// Resumo do condomínio (P-17).
//
// A aba tinha três blocos de `defaultValue` — identificação, administradora e
// síndico — e um botão "Salvar" sem `onClick`. Digitar o nome do síndico,
// clicar em Salvar e dar F5 devolvia o valor antigo, sem nenhum aviso de que
// nada tinha sido gravado.
//
// Só o endereço gravava, por um segundo botão dentro do próprio bloco. Os dois
// viraram uma gravação só: dois botões "Salvar" na mesma tela, um deles com
// escopo escondido, é como alguém salva metade do formulário achando que salvou
// tudo.
//
// `comentarios` também era pedido duas vezes — no comentário do bloco de
// endereço e num campo "Comentários" no fim da aba — os dois escrevendo na
// mesma coluna. Ficou o do endereço, que é onde a construção compartilhada o
// coloca em todos os outros cadastros.

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import CampoEndereco from '@/components/base/CampoEndereco';
import { ENDERECO_VAZIO, type Endereco } from '@/lib/endereco';
import {
  limparDocumento,
  mascaraDocumento,
  mascaraTelefone,
  MENSAGEM,
  validarEmail,
  validarTelefone,
} from '@/lib/mascaras';
import { ateOuIgual, dataValida, MENSAGEM_DATA, noFuturo, noPassado } from '@/lib/datas';

interface Contato {
  nome?: string;
  email?: string;
  telefone?: string;
}

interface Administradora {
  nome?: string;
  cnpj?: string;
  telefone?: string;
  whatsapp?: string;
  email?: string;
  dataInicioContrato?: string;
  dataFimContrato?: string;
  observacoes?: string;
  contato?: Contato;
}

interface Sindico {
  nome?: string;
  cpf?: string;
  telefone?: string;
  email?: string;
  dataInicioMandato?: string;
  dataFimMandato?: string;
  historico?: Array<{ nome: string; dataInicio: string; dataFim: string }>;
}

interface CondoData {
  id: string;
  nome: string;
  razao_social?: string;
  comentarios?: string;
  endereco?: Partial<Endereco>;
  administradora?: Administradora;
  sindico?: Sindico;
}

interface ResumoTabProps {
  condo: CondoData;
  /** Recarrega o condomínio no componente pai depois de gravar. */
  onSalvo?: () => void;
}

function formatData(iso: string) {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const CLASSE_CAMPO =
  'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm';

export default function ResumoTab({ condo, onSalvo }: ResumoTabProps) {
  const [mostrarHistorico, setMostrarHistorico] = useState(false);

  const [nome, setNome] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [endereco, setEndereco] = useState<Endereco>(ENDERECO_VAZIO);
  const [admin, setAdmin] = useState<Administradora>({});
  const [sindico, setSindico] = useState<Sindico>({});

  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ texto: string; tipo: 'ok' | 'erro' } | null>(null);

  // Semeado quando o condomínio chega, e não no primeiro render: inicializar no
  // `useState` prenderia os campos no estado anterior ao carregamento.
  useEffect(() => {
    setNome(condo.nome ?? '');
    setRazaoSocial(condo.razao_social ?? '');
    setEndereco({
      ...ENDERECO_VAZIO,
      ...(condo.endereco ?? {}),
      comentario: condo.comentarios ?? '',
    });
    setAdmin(condo.administradora ?? {});
    setSindico(condo.sindico ?? {});
    setAviso(null);
  }, [condo]);

  const alterarAdmin = (campo: keyof Administradora, valor: string) =>
    setAdmin((atual) => ({ ...atual, [campo]: valor }));

  const alterarContato = (campo: keyof Contato, valor: string) =>
    setAdmin((atual) => ({ ...atual, contato: { ...(atual.contato ?? {}), [campo]: valor } }));

  const alterarSindico = (campo: keyof Sindico, valor: string) =>
    setSindico((atual) => ({ ...atual, [campo]: valor }));

  // Validação antes de ir ao banco. O mandato tem regra no banco desde a
  // migration 0014; aqui ela aparece na hora da digitação em vez de voltar como
  // erro de constraint.
  const erros = useMemo(() => {
    const lista: string[] = [];

    if (!nome.trim()) lista.push('O nome do condomínio é obrigatório.');

    const datas: Array<[string | undefined, string]> = [
      [admin.dataInicioContrato, 'Início do contrato'],
      [admin.dataFimContrato, 'Término do contrato'],
      [sindico.dataInicioMandato, 'Início do mandato'],
      [sindico.dataFimMandato, 'Fim do mandato'],
    ];
    for (const [valor, rotulo] of datas) {
      if (valor && !dataValida(valor)) lista.push(`${rotulo}: ${MENSAGEM_DATA.invalida}`);
    }

    if (
      admin.dataInicioContrato &&
      admin.dataFimContrato &&
      !ateOuIgual(admin.dataInicioContrato, admin.dataFimContrato)
    ) {
      lista.push(`Contrato da administradora: ${MENSAGEM_DATA.fimAntesDoInicio}`);
    }

    if (
      sindico.dataInicioMandato &&
      sindico.dataFimMandato &&
      !ateOuIgual(sindico.dataInicioMandato, sindico.dataFimMandato)
    ) {
      lista.push(`Mandato do síndico: ${MENSAGEM_DATA.fimAntesDoInicio}`);
    }

    if (sindico.dataInicioMandato && noFuturo(sindico.dataInicioMandato)) {
      lista.push('Início do mandato: o mandato atual não pode começar no futuro.');
    }

    // Contrato da administradora: o bloco descreve **a administradora atual**,
    // e não um histórico. Um contrato que começa amanhã ou que terminou ontem
    // não descreve quem responde pelo condomínio hoje — e, gravado aqui, é o
    // que a tela e os relatórios passam a afirmar.
    if (admin.dataInicioContrato && noFuturo(admin.dataInicioContrato)) {
      lista.push(`Início do contrato: ${MENSAGEM_DATA.naoPodeSerFutura}`);
    }

    if (admin.dataFimContrato && noPassado(admin.dataFimContrato)) {
      lista.push(
        `Término do contrato: ${MENSAGEM_DATA.naoPodeSerPassada} Para registrar que a administradora saiu, troque os dados pelos da atual.`,
      );
    }

    // Telefone e e-mail: os campos são opcionais, então só se valida o que foi
    // preenchido. As regras e as frases vêm de `lib/mascaras`, as mesmas do
    // cadastro de prestador e do registro — telefone meio digitado gravado aqui
    // reaparece depois num relatório ou num envio que falha em silêncio.
    const telefones: Array<[string | undefined, string]> = [
      [admin.telefone, 'Telefone da administradora'],
      [admin.whatsapp, 'WhatsApp da administradora'],
      [admin.contato?.telefone, 'Telefone do contato'],
      [sindico.telefone, 'Telefone do síndico'],
    ];
    for (const [valor, rotulo] of telefones) {
      if (valor?.trim() && !validarTelefone(valor)) lista.push(`${rotulo}: ${MENSAGEM.telefone}`);
    }

    const emails: Array<[string | undefined, string]> = [
      [admin.email, 'E-mail da administradora'],
      [admin.contato?.email, 'E-mail do contato'],
      [sindico.email, 'E-mail do síndico'],
    ];
    for (const [valor, rotulo] of emails) {
      if (valor?.trim() && !validarEmail(valor)) lista.push(`${rotulo}: ${MENSAGEM.email}`);
    }

    return lista;
  }, [nome, admin, sindico]);

  const salvar = async () => {
    if (erros.length > 0) {
      setAviso({ texto: erros[0], tipo: 'erro' });
      return;
    }

    setSalvando(true);
    setAviso(null);

    const { comentario, ...enderecoEstruturado } = endereco;

    // `localizacao` não é enviada: o banco a deriva de cidade/estado
    // (migration 0010). Enviá-la daqui abriria a chance de divergir do endereço.
    const { error } = await supabase
      .from('condominios')
      .update({
        nome: nome.trim(),
        razao_social: razaoSocial.trim() || null,
        endereco: enderecoEstruturado,
        comentarios: comentario?.trim() || null,
        administradora: admin,
        // O histórico de mandatos é preservado: a tela edita o mandato atual, e
        // sobrescrever o objeto inteiro apagaria os anteriores.
        sindico: { ...sindico, historico: condo.sindico?.historico ?? sindico.historico ?? [] },
        updated_at: new Date().toISOString(),
      })
      .eq('id', condo.id);

    setSalvando(false);

    if (error) {
      setAviso({ texto: `Não foi possível salvar: ${error.message}`, tipo: 'erro' });
      return;
    }

    setAviso({ texto: 'Alterações salvas.', tipo: 'ok' });
    onSalvo?.();
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Identificação */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Identificação</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="condo-nome"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Nome do Condomínio
            </label>
            <input
              id="condo-nome"
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="condo-razao"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Razão Social
            </label>
            <input
              id="condo-razao"
              type="text"
              value={razaoSocial}
              onChange={(e) => setRazaoSocial(e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
        </div>
      </div>

      {/* Endereço — mesma construção usada em todos os cadastros */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Endereço</h3>
        <CampoEndereco
          valor={endereco}
          onChange={setEndereco}
          comComentario
          rotuloComentario="Comentário"
          idPrefixo="condominio"
        />
      </div>

      {/* Administradora */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded bg-accent-100 flex items-center justify-center">
            <i className="ri-building-2-line text-accent-700 text-xs"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-900">Administradora</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-3">
            <label
              htmlFor="admin-nome"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Nome da Administradora
            </label>
            <input
              id="admin-nome"
              type="text"
              placeholder="Ex: Alfa Administração de Condomínios Ltda."
              value={admin.nome ?? ''}
              onChange={(e) => alterarAdmin('nome', e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="admin-cnpj"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              CNPJ
            </label>
            <input
              id="admin-cnpj"
              type="text"
              autoCapitalize="characters"
              placeholder="12.345.678/0001-90"
              value={mascaraDocumento(admin.cnpj ?? '', 'pj')}
              onChange={(e) => alterarAdmin('cnpj', limparDocumento(e.target.value, 'pj'))}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="admin-telefone"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Telefone
            </label>
            <input
              id="admin-telefone"
              type="text"
              inputMode="numeric"
              maxLength={15}
              placeholder="(11) 3456-7890"
              value={admin.telefone ?? ''}
              onChange={(e) => alterarAdmin('telefone', mascaraTelefone(e.target.value))}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="admin-whatsapp"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              <span className="inline-flex items-center gap-1">
                WhatsApp
                <span className="text-foreground-400 font-normal">(opcional)</span>
              </span>
            </label>
            <input
              id="admin-whatsapp"
              type="text"
              inputMode="numeric"
              maxLength={15}
              placeholder="(11) 3456-7890"
              value={admin.whatsapp ?? ''}
              onChange={(e) => alterarAdmin('whatsapp', mascaraTelefone(e.target.value))}
              className={CLASSE_CAMPO}
            />
          </div>
          <div className="md:col-span-3">
            <label
              htmlFor="admin-email"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              E-mail
            </label>
            <input
              id="admin-email"
              type="email"
              placeholder="contato@administradora.com.br"
              value={admin.email ?? ''}
              onChange={(e) => alterarAdmin('email', e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="admin-inicio"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Início do Contrato
            </label>
            <input
              id="admin-inicio"
              type="date"
              value={admin.dataInicioContrato ?? ''}
              onChange={(e) => alterarAdmin('dataInicioContrato', e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="admin-fim"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Término do Contrato
              <span className="text-foreground-400 font-normal ml-1">(opcional)</span>
            </label>
            <input
              id="admin-fim"
              type="date"
              value={admin.dataFimContrato ?? ''}
              onChange={(e) => alterarAdmin('dataFimContrato', e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
        </div>

        {/* Contato da Administradora */}
        <div className="mt-6 pt-5 border-t border-background-200/70">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded bg-secondary-100 flex items-center justify-center">
              <i className="ri-user-voice-line text-secondary-700 text-xs"></i>
            </div>
            <h3 className="text-sm font-semibold text-foreground-900">Contato na Administradora</h3>
            <span className="text-xs text-foreground-400">(opcional)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="contato-nome"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Nome do Contato
              </label>
              <input
                id="contato-nome"
                type="text"
                placeholder="Ex: Maria Souza"
                value={admin.contato?.nome ?? ''}
                onChange={(e) => alterarContato('nome', e.target.value)}
                className={CLASSE_CAMPO}
              />
            </div>
            <div>
              <label
                htmlFor="contato-telefone"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Telefone do Contato
              </label>
              <input
                id="contato-telefone"
                type="text"
                inputMode="numeric"
                maxLength={15}
                placeholder="(11) 3456-7890"
                value={admin.contato?.telefone ?? ''}
                onChange={(e) => alterarContato('telefone', mascaraTelefone(e.target.value))}
                className={CLASSE_CAMPO}
              />
            </div>
            <div className="md:col-span-2">
              <label
                htmlFor="contato-email"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                E-mail do Contato
              </label>
              <input
                id="contato-email"
                type="email"
                placeholder="contato@administradora.com.br"
                value={admin.contato?.email ?? ''}
                onChange={(e) => alterarContato('email', e.target.value)}
                className={CLASSE_CAMPO}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-background-200/70">
          <label
            htmlFor="admin-obs"
            className="block text-sm font-medium text-foreground-700 mb-1.5"
          >
            Observações
          </label>
          <textarea
            id="admin-obs"
            value={admin.observacoes ?? ''}
            onChange={(e) => alterarAdmin('observacoes', e.target.value)}
            maxLength={1000}
            rows={2}
            className={`${CLASSE_CAMPO} resize-y`}
          />
        </div>
      </div>

      {/* Síndico */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded bg-primary-50 flex items-center justify-center">
            <i className="ri-user-star-line text-primary-600 text-xs"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-900">Síndico</h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
            Mandato atual
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label
              htmlFor="sindico-nome"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Nome completo
            </label>
            <input
              id="sindico-nome"
              type="text"
              placeholder="Ex: João Pereira da Silva"
              value={sindico.nome ?? ''}
              onChange={(e) => alterarSindico('nome', e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="sindico-cpf"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              CPF
            </label>
            <input
              id="sindico-cpf"
              type="text"
              inputMode="numeric"
              placeholder="123.456.789-00"
              value={mascaraDocumento(sindico.cpf ?? '', 'pf')}
              onChange={(e) => alterarSindico('cpf', limparDocumento(e.target.value, 'pf'))}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="sindico-telefone"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Telefone
            </label>
            <input
              id="sindico-telefone"
              type="text"
              inputMode="numeric"
              maxLength={15}
              placeholder="(11) 3456-7890"
              value={sindico.telefone ?? ''}
              onChange={(e) => alterarSindico('telefone', mascaraTelefone(e.target.value))}
              className={CLASSE_CAMPO}
            />
          </div>
          <div className="md:col-span-2">
            <label
              htmlFor="sindico-email"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              E-mail
            </label>
            <input
              id="sindico-email"
              type="email"
              placeholder="contato@administradora.com.br"
              value={sindico.email ?? ''}
              onChange={(e) => alterarSindico('email', e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="sindico-inicio"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Início do Mandato
            </label>
            <input
              id="sindico-inicio"
              type="date"
              value={sindico.dataInicioMandato ?? ''}
              onChange={(e) => alterarSindico('dataInicioMandato', e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="sindico-fim"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Fim do Mandato
            </label>
            <input
              id="sindico-fim"
              type="date"
              value={sindico.dataFimMandato ?? ''}
              onChange={(e) => alterarSindico('dataFimMandato', e.target.value)}
              className={CLASSE_CAMPO}
            />
          </div>
        </div>

        {/* Histórico de mandatos */}
        <div className="mt-5 pt-5 border-t border-background-200/70">
          <button
            type="button"
            onClick={() => setMostrarHistorico(!mostrarHistorico)}
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors"
          >
            <div
              className={`w-4 h-4 flex items-center justify-center transition-transform ${mostrarHistorico ? 'rotate-90' : ''}`}
            >
              <i className="ri-arrow-right-s-line"></i>
            </div>
            Histórico de Mandatos
            <span className="text-xs text-foreground-400">
              ({condo.sindico?.historico?.length || 0})
            </span>
          </button>

          {mostrarHistorico && (
            <div className="mt-4 pl-2">
              {(condo.sindico?.historico || []).length === 0 ? (
                <p className="text-sm text-foreground-500">
                  Nenhum mandato anterior registrado. O mandato encerrado entra no histórico pelo
                  gatilho do banco quando um novo síndico assume.
                </p>
              ) : (
                <div className="relative">
                  <div className="absolute left-[15px] top-2 bottom-2 w-px bg-background-200/70"></div>
                  <div className="flex flex-col gap-3">
                    {(condo.sindico?.historico || []).map((mandato, idx) => (
                      <div
                        key={`${mandato.nome}-${mandato.dataInicio}`}
                        className="flex items-start gap-3 relative"
                      >
                        <div
                          className={`relative z-10 mt-1 w-3 h-3 rounded-full border-2 flex-shrink-0 ${
                            idx === 0
                              ? 'bg-primary-500 border-primary-500'
                              : 'bg-background-50 border-background-300'
                          }`}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-800">{mandato.nome}</p>
                          <p className="text-xs text-foreground-500">
                            {formatData(mandato.dataInicio)} — {formatData(mandato.dataFim)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {aviso && (
          <span
            className={`text-sm flex items-center gap-1.5 ${aviso.tipo === 'ok' ? 'text-green-700' : 'text-red-700'}`}
          >
            <i className={aviso.tipo === 'ok' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
            {aviso.texto}
          </span>
        )}
        <button
          type="button"
          onClick={salvar}
          disabled={salvando}
          className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
